import React, { useState } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatic from 'hoist-non-react-statics';
import Field from '../../../../../modules/react/components/stateful/Field';
import FacilitySelect from '../../../../../admin/containers/FacilitySelect';
import widgetDecorator from './widget';
import editModal from './editModal';
import contextMenu from './contextMenu';

export const propKey = '@@facilityWidget';

/**
 * A decorator/HOC used to describe a facility widget component. This HOC will
 * wrap the normal widget HOC and also provide some facility auto-looking magic
 * to provide the `facilityId` and some other facility details as properties to
 * your component.
 *
 * These options are the same accepted by the {@link widgetDecorator} decorator/HOC,
 * so check that documentation for more information.
 */
export default (details, { resolve = null, ...options } = {}) => {
  const createHoc = (Component) => {
    // Apply the widget decorator to the passed component
    const WidgetComponent = widgetDecorator(details, { ...options, resolve })(Component);

    // This component will act as a placeholder in the event that the widget does not have a facility ID
    const PlaceholderWidget = () => (
      <div>
        Please set the facility
      </div>
    );

    const PlaceholderFacilitySelect = ({ facilityId, input: { onChange } }) => {
      const [selectedFacilityId, setFacilityId] = useState(facilityId);

      const onFacilityChange = ({ value }) => {
        onChange(value);
        setFacilityId(value);
      };

      return (
        <FacilitySelect
          clearable={false}
          onChange={onFacilityChange}
          value={selectedFacilityId}
        />
      );
    };

    const PlaceholderFacilityWidget = editModal(
      {},
      () => [
        {
          children: (
            <Field
              label="Facility"
              name="facilityId"
              required
              placeholder="Select a facility"
              component={PlaceholderFacilitySelect}
            />
          ),
        },
      ],
    )(contextMenu()(hoistNonReactStatic(PlaceholderWidget, WidgetComponent)));

    // This wrapper component will show the placeholder component if no facility ID can be determined
    const FacilityWidget = (props) => {
      const { facilityId, pageFacilityId } = props;

      if (!facilityId && !pageFacilityId) {
        // render the facility selector if using this widget on a non-facility page
        return <PlaceholderFacilityWidget {...props} />;
      }

      // We either use facilityId (from widget payload) or pageFacilityId (which comes from a facility page)
      const newProps = {
        ...props,
        facilityId: facilityId || pageFacilityId,
      };

      return <WidgetComponent {...newProps} />;
    };

    FacilityWidget.propTypes = {
      facilityId: PropTypes.string,
    };

    FacilityWidget.defaultProps = {
      facilityId: null,
    };

    FacilityWidget.displayName = `FacilityWidget(${WidgetComponent.displayName})`;

    return (hoistNonReactStatic(FacilityWidget, WidgetComponent));
  };

  if (process.env.NODE_ENV === 'testing') {
    createHoc.$$voyagerTestWhitelist = true;
  }

  return createHoc;
};
